"use client";

import { c as _c } from "react/compiler-runtime";
import { getDiscountName } from "@/lib/discount-utils";
import { formatCurrency } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import type { DiscountDetail } from "@/types/billing/discount";
interface Props {
  benefits: DiscountDetail[];
  className?: string;
}
interface UsageRow {
  key: string;
  name: string;
  usage: string;
  isUsedUp: boolean;
}
function isUsedUp(benefit: DiscountDetail): boolean {
  if (benefit.creditPool) return benefit.creditPool.creditsRemaining <= 0;
  if (benefit.quantity == null) return false;
  return (benefit.quantityRemaining ?? benefit.quantity) <= 0;
}

// Using up a limited benefit archives it, so a used-up row stays listed. Any
// other archived row is an option not chosen from its group, or a benefit
// retired by a cancellation, and is no longer the patient's. Membership
// benefits also say whether checkout would take them now (not while the
// membership is frozen or pending), and those it would refuse are hidden.
function isCurrent(benefit: DiscountDetail, now: number): boolean {
  if (isUsedUp(benefit)) {
    return !benefit.expiryDate || new Date(benefit.expiryDate).getTime() >= now;
  }
  if (benefit.isArchived || benefit.isRedeemable === false) return false;
  return !benefit.expiryDate || new Date(benefit.expiryDate).getTime() >= now;
}

// The same benefit across a membership's cycles: each cycle issues it again,
// and a choice group's options share one allowance.
function benefitIdentity(benefit: DiscountDetail): string {
  return benefit.discountChoiceId ?? benefit.discountTemplateId ?? [benefit.itemId, benefit.category, benefit.itemType].join(":");
}

/**
 * A used-up membership row stays until its own expiry, which with rollover is
 * well after its cycle ends (or never). Keep one used-up record per benefit,
 * from its latest issuance, and none once a newer allowance of it is open.
 * Packages issue their benefits once, so they are left as they are.
 */
function withoutStaleUsedUp(benefits: DiscountDetail[]): DiscountDetail[] {
  const byBenefit = new Map<string, DiscountDetail[]>();
  for (const benefit of benefits) {
    if (!benefit.memberId) continue;
    const key = `${benefit.memberId}:${benefitIdentity(benefit)}`;
    byBenefit.set(key, [...(byBenefit.get(key) ?? []), benefit]);
  }
  const stale = new Set<string>();
  byBenefit.forEach(rows => {
    const usedUp = rows.filter(isUsedUp);
    if (!usedUp.length) return;
    if (rows.some(row => !isUsedUp(row))) {
      usedUp.forEach(row => stale.add(row.id));
      return;
    }
    const newest = usedUp.reduce((latest, row) => (row.createdDate ?? "") > (latest.createdDate ?? "") ? row : latest);
    const issuance = (row: DiscountDetail) => row.creditPool?.id ?? row.membershipCycleId ?? row.id;
    usedUp.forEach(row => {
      if (issuance(row) !== issuance(newest)) stale.add(row.id);
    });
  });
  return benefits.filter(benefit => !stale.has(benefit.id));
}

// A benefit with no target covers everything checkout sells, which
// getDiscountName leaves as a bare "Benefit".
function benefitName(benefit: DiscountDetail): string {
  if (!benefit.item && !benefit.category && !benefit.itemType) {
    return "All items";
  }
  return getDiscountName(benefit);
}

// With no count to spend, a benefit that only takes part of the price off
// says how much, so "Unlimited" is never read as unlimited free visits.
function uncappedUsage(benefit: DiscountDetail): string {
  if (benefit.percentage && benefit.percentage < 100) {
    return `${benefit.percentage}% off`;
  }
  if (!benefit.percentage && benefit.amount) {
    return `${formatCurrency(benefit.amount)} off`;
  }
  return "Unlimited";
}
function limitedUsage(benefit: DiscountDetail): Omit<UsageRow, "key" | "name"> {
  if (benefit.quantity == null) {
    return {
      usage: uncappedUsage(benefit),
      isUsedUp: false
    };
  }
  const remaining = benefit.quantityRemaining ?? benefit.quantity;
  return {
    usage: `${remaining} of ${benefit.quantity} left`,
    isUsedUp: remaining <= 0
  };
}

/**
 * The "choose one" groups among the benefits, keyed per issuance. A
 * membership's group is issued again each cycle and a package can be sold
 * twice, so each cycle and each sale is its own group. Older options with no
 * cycle recorded belong to every cycle's group, as they do when checkout
 * settles a choice, but only while they are still open: a used-up old pick
 * says nothing about a newer cycle's allowance.
 */
function choiceGroups(benefits: DiscountDetail[]) {
  const bySource = new Map<string, DiscountDetail[]>();
  for (const benefit of benefits) {
    if (benefit.creditPool || !benefit.discountChoiceId) continue;
    const source = `${benefit.discountChoiceId}:${benefit.soldPackageId ?? ""}`;
    bySource.set(source, [...(bySource.get(source) ?? []), benefit]);
  }
  const groups = new Map<string, DiscountDetail[]>();
  const groupsOf = new Map<string, string[]>();
  bySource.forEach((options, source) => {
    const cycles = Array.from(new Set(options.map(o => o.membershipCycleId).filter((cycle): cycle is string => !!cycle)));
    for (const cycle of cycles.length ? cycles : [""]) {
      const key = `${source}:${cycle}`;
      const members = options.filter(o => o.membershipCycleId ? o.membershipCycleId === cycle : cycle === "" || !isUsedUp(o));
      groups.set(key, members);
      for (const member of members) {
        groupsOf.set(member.id, [...(groupsOf.get(member.id) ?? []), key]);
      }
    }
  });
  return {
    groups,
    groupsOf
  };
}

// Once an option is chosen its count is the group's. Before that the options
// may grant different amounts, so the row only gives a number they share.
function choiceUsage(options: DiscountDetail[]) {
  const chosen = options.find(o => o.isChosen);
  if (chosen) return limitedUsage(chosen);
  const usages = options.map(limitedUsage);
  return usages.every(u => u.usage === usages[0].usage) ? usages[0] : {
    usage: "Choose one",
    isUsedUp: false
  };
}

/**
 * One row per benefit. Options that spend from the same credit pool share one
 * balance, and the options of a "choose one" group share one allowance, so
 * each collapses into a single row.
 */
function toUsageRows(benefits: DiscountDetail[]): UsageRow[] {
  const now = Date.now();
  const current = withoutStaleUsedUp(benefits.filter(benefit => isCurrent(benefit, now)));
  const {
    groups,
    groupsOf
  } = choiceGroups(current);
  const rows: UsageRow[] = [];
  const seen = new Set<string>();
  for (const benefit of current) {
    const pool = benefit.creditPool;
    if (pool) {
      if (seen.has(`pool-${pool.id}`)) continue;
      seen.add(`pool-${pool.id}`);
      rows.push({
        key: `pool-${pool.id}`,
        name: benefit.group || "Credits",
        usage: `${pool.creditsRemaining} of ${pool.credits} credits left`,
        isUsedUp: pool.creditsRemaining <= 0
      });
      continue;
    }
    const groupKeys = (groupsOf.get(benefit.id) ?? []).filter(key => (groups.get(key)?.length ?? 0) > 1);
    if (groupKeys.length) {
      for (const groupKey of groupKeys) {
        const key = `choice-${groupKey}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const options = groups.get(groupKey) ?? [];
        rows.push({
          key,
          name: benefit.group || options.map(benefitName).join(" or "),
          ...choiceUsage(options)
        });
      }
      continue;
    }
    rows.push({
      key: benefit.id,
      name: benefitName(benefit),
      ...limitedUsage(benefit)
    });
  }
  return rows;
}
export function BenefitUsageList(t0) {
  const $ = _c(10);
  const {
    benefits,
    className
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== benefits || $[1] !== className) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const rows = toUsageRows(benefits);
      if (rows.length === 0) {
        t3 = null;
        break bb0;
      }
      if ($[5] !== className) {
        t1 = cx("mt-2 space-y-1", className);
        $[5] = className;
        $[6] = t1;
      } else {
        t1 = $[6];
      }
      t2 = rows.map(_temp);
    }
    $[0] = benefits;
    $[1] = className;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2) {
    t4 = <ul className={t1}>{t2}</ul>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function _temp(row) {
  return <li key={row.key} className="flex items-center justify-between gap-2 text-caption"><span className="truncate text-foreground-secondary">{row.name}</span><span className={cx("flex-shrink-0", row.isUsedUp ? "text-muted-foreground" : "text-foreground")}>{row.usage}</span></li>;
}